import React, {useState, useEffect} from "react";
import {Form, Button, Table, Col, Row, Modal, Alert} from "react-bootstrap";
import {useNavigate, Link} from "react-router-dom";
import {DashboardHero, DashboardMetrics, formatAmount} from '../dashboardui';
import {canViewCustomerReports} from './customerreports';
import '../operationsui.css';
import '../../App.css';
// import {addCustomer, updateCustomer, deleteCustomer, updateCustomerBalance, createTransaction, fetchAccounts} from "../../api/api";
import {addCustomer, updateCustomer, deleteCustomer} from "../../api/api";
import {useCustomers} from "../../CustomerContext";
import {useAccounts} from "../../contextjs/AccountContext";
import {useTransactions} from "../../contextjs/TransactionContext";
import { useUser } from "../../UserContext";
import { hasPermission } from "../utils/permissions";
import { apiInstance } from "../../api/api";
import { fetchStoreAccountSettings } from "../../api/api";

const Customer = ({paymentsOnly = false}) => {
    // states from CustomerContext.js
    const { customers, fetchCustomers } = useCustomers();
    const { loadTransactions } = useTransactions();
    const { fetchAccountsData } = useAccounts();
    const [showModal, setShowModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        customer_name: '',
        location: '',
        phone_number: '',
        customer_balance_limit: 0
    });

    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState({}); // state to store the selected customer for editing
    const [searchTerm, setSearchTerm] = useState(''); // search term for customers
    // state for payment amount and selected customer for payment
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [paymentNotes, setPaymentNotes] = useState('');
    const [paymentSettings, setPaymentSettings] = useState([])
    const [paymentCustomer, setPaymentCustomer] = useState(null);
    const [paymentBusy, setPaymentBusy] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [paymentNotice, setPaymentNotice] = useState('');
    const openPayment = customer => {setPaymentCustomer(customer); setPaymentAmount(''); setPaymentMethod(''); setPaymentNotes(''); setPaymentError(''); setPaymentNotice('');};
    const { user } = useUser();
    // filter balances
    const [filterBalance, setFilterBalance] = useState(paymentsOnly);
    const [filterExcessPay, setFilterExcessPay] = useState(false);

    const handleBack = () => {
        navigate(-1);
    };

    // fetchCustomers from CustomerContext.js
    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        fetchStoreAccountSettings({ usage_type: 'PAYMENT'})
            .then((settings) => setPaymentSettings(settings.filter((setting) => setting.is_enabled)))
            .catch((error) => console.error('Unable to load customer payment accounts', error));
    }, []);


    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);

    const handleChange = (e) => {
        setNewCustomer({...newCustomer, [e.target.name]: e.target.value});
    };

    const handleShowEditModal = (customer) => {
        setSelectedCustomer(customer);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => setShowEditModal(false);

    const handleEditChange = (e) => {
        const {name, value} = e.target;
        setSelectedCustomer({...selectedCustomer, [name]: value});
    };

    const handleUpdateCustomer = async (e) => {
        e.preventDefault();
        const updatedCustomer = await updateCustomer(selectedCustomer); // updates customer in the backend
        // const updatedCustomers = customers.map(customer => customer.id === updatedCustomer.id ? updatedCustomer : customer);
        // setCustomers(updatedCustomers);
        console.log('Updated customer:', updatedCustomer);
        fetchCustomers(); // re-fetch customers to update global state
        handleCloseEditModal();
    };


    const handleDeleteCustomer = async (customerId) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this customer?');
        if(isConfirmed) {
            try {
                await deleteCustomer(customerId); // delete customer in the backend
                fetchCustomers(); // re-fetch customers to update global state
            } catch (error) {
                alert(error?.response?.data?.detail || 'This customer cannot be deleted.');
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addCustomer(newCustomer);
            setNewCustomer({
                customer_name: '',
                location: '',
                phone_number: '',
                customer_balance_limit: 0
            });
            handleClose();
        } catch (error) {
            console.log('Error adding customer:', error);
        }
        fetchCustomers(); // re-fetch customers to update global state
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // handler for filtering customers with balances
    const handleFilterBalanceChange = (e) => {
        setFilterBalance(e.target.checked);
        if (e.target.checked) setFilterExcessPay(false); // uncheck excess pay filter if balance filter is checked
    };

    const handleFilterExcessPayChange = (e) => {
        setFilterExcessPay(e.target.checked);
        if (e.target.checked) setFilterBalance(false); // uncheck balance filter if excess pay filter is checked
    };

    const filteredCustomers = customers
        .filter(customer => {
            const matchesSearch = customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase());
            const bal = Number(customer.customer_balance ?? 0);
            const matchesBalanceFilter = !filterBalance || (Number.isFinite(bal) && bal > 0);
            const matchesExcessPayFilter = !filterExcessPay || (Number.isFinite(bal) && bal < 0);
            return matchesSearch && matchesBalanceFilter && matchesExcessPayFilter;
        })
        .sort((left, right) => {
            if (!filterBalance && !paymentsOnly) return 0;
            return Number(right.customer_balance || 0) - Number(left.customer_balance || 0);
        });

    const handleReceivePayment = async (customer) => {
      if (paymentBusy) return;
      setPaymentError('');
      if (!paymentAmount || !Number.isFinite(Number(paymentAmount)) || Number(paymentAmount) <= 0) {
        setPaymentError('Please enter a valid payment amount');
        return;
      }
      if (!paymentMethod) {
        setPaymentError('Please select a payment method');
        return;
      }

      setPaymentBusy(true);
      try {
          const selectedSetting = paymentSettings.find(
              (setting) => String(setting.account) === String(paymentMethod)
          );
          const legacyMethod = paymentMethod.startsWith('legacy:')
              ? paymentMethod.replace('legacy:', '')
              : null;
          const response = await apiInstance.post(`/customers/${customer.id}/receive-payment/`, {
              payment_method: selectedSetting?.payment_channel?.toLowerCase() || legacyMethod || 'other',
              payment_account: selectedSetting?.account,
              payment_amount: paymentAmount,
              notes: paymentNotes.trim()
          });
            console.log('Payment successful:', response.data);

            fetchCustomers(); // re-fetch customers to update global state
            setPaymentAmount('');
            setPaymentMethod('');
            setPaymentNotes('');
            setPaymentCustomer(null);
            setPaymentNotice(`Payment of KES ${formatAmount(paymentAmount)} received from ${customer.customer_name}.`);

            loadTransactions(); // re-fetch transactions to update the global state
            fetchAccountsData(); // re-fetch accounts to update global state
      } catch (error) {
          console.error('Error receiving payment:', error);
          setPaymentError(error?.response?.data?.error || error?.response?.data?.detail || 'Unable to confirm payment. Check the ledger before retrying if the connection was interrupted.');
      } finally {
          setPaymentBusy(false);
      }
    };

    // define permission-checking functions
    const canAddCustomer = () => {
        return hasPermission(user, 'BeeqasiApp.add_customer');
        // user.is_superuser || user.groups.includes("Admin")
    };

    const canEditCustomer = () => {
        return hasPermission(user, 'BeeqasiApp.change_customer');
        // user.is_superuser || user.groups.includes("Admin")
    };

    const canDeleteCustomer = (customer) => {
        const isAdmin = Boolean(
            user?.is_superuser ||
            user?.groups?.includes?.('Admin') ||
            user?.groups?.includes?.('Administrator')
        );
        return isAdmin && customer?.can_delete;
    };

    if (paymentsOnly && !canEditCustomer()) return <Alert variant="info">You do not have permission to receive customer payments.</Alert>;
    return (
        <div className={`wh-dashboard ops-page ${paymentsOnly ? 'customer-payments-page' : ''}`}>
            <div className="back-button-container">
                <Button variant="secondary" onClick={handleBack}>Back</Button>
            </div>
            <DashboardHero eyebrow="Customer relationships" title={paymentsOnly ? 'Receive payments' : 'Customers'} description={paymentsOnly ? 'Find a customer, check their balance and receive payment. Balances are company-wide; payments use the active store.' : 'Manage customer details, collect payments and keep account exposure in view.'} action={paymentsOnly ? 'Customer directory' : canViewCustomerReports(user) ? 'Customer reports' : 'Home'} to={paymentsOnly ? '/CRM/customers' : canViewCustomerReports(user) ? '/CRM/customer-reports' : '/'} extraAction={!paymentsOnly && canEditCustomer() ? <Link className="dash-primary-action" to="/CRM/customer-payments">Receive payment <i className="bi bi-wallet2" /></Link> : null} />
            {paymentNotice && <Alert variant="success" dismissible onClose={() => setPaymentNotice('')}>{paymentNotice}</Alert>}
            {!paymentsOnly && <DashboardMetrics items={[
                {label:'Customer directory',value:customers.length,hint:'All registered customers',icon:'bi-people'},
                {label:'Outstanding balances',value:`KES ${formatAmount(customers.reduce((sum, c) => sum + Math.max(0, Number(c.customer_balance || 0)), 0))}`,hint:'Current customer balances · company-wide',icon:'bi-wallet2',tone:'amber'},
                {label:'Customer credits',value:`KES ${formatAmount(customers.reduce((sum, c) => sum + Math.max(0, -Number(c.customer_balance || 0)), 0))}`,hint:'Credit balances available on account',icon:'bi-credit-card',tone:'blue'},
            ]} />}
            {!paymentsOnly && canAddCustomer() && (
                <Button variant="primary" onClick={handleShow} className="mb-3">
                    Add Customer</Button>
            )}

            {/* Search input */}
            <Row className="filter-section g-3 mb-3 align-items-center">
                <Col md={6}>
                    <Form.Control
                        type="text"
                        placeholder="Search customer..."
                        aria-label="Search customer name"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="checkbox"
                        label="Show Balances > 0"
                        id="customer-positive-balances"
                        checked={filterBalance}
                        onChange={handleFilterBalanceChange}
                    />
                </Col>
                <Col md="auto">
                    <Form.Check
                        type="checkbox"
                        label="Show Excess Pay < 0"
                        id="customer-credit-balances"
                        checked={filterExcessPay}
                        onChange={handleFilterExcessPayChange}
                    />
                </Col>
            </Row>

            <div className="wh-section-heading"><h2>{paymentsOnly ? 'Customer payments' : 'Customer directory'}</h2><span>{filteredCustomers.length} matching customers</span></div>
            {paymentsOnly ? <Table hover className="customer-payments-table">
                <colgroup><col style={{width:'40%'}} /><col style={{width:'32%'}} /><col style={{width:'28%'}} /></colgroup>
                <thead><tr><th>Name</th><th>Balance (KES)</th><th>Payment</th></tr></thead>
                <tbody>{filteredCustomers.map(customer => <tr key={customer.id}><td>{customer.customer_name}</td><td className="payment-balance">{formatAmount(customer.customer_balance)}</td><td><Button variant="success" onClick={() => openPayment(customer)} aria-label={`Receive payment from ${customer.customer_name}`}>Receive</Button></td></tr>)}
                {!filteredCustomers.length && <tr><td colSpan={3} className="text-muted text-center py-4">No customers match these filters.</td></tr>}</tbody>
            </Table> : <Table responsive hover>
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Phone Number</th>
                    <th>Balance</th>
                    <th>Customer Limit</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredCustomers.map((customer, index) => (
                    <tr key={customer.id}>
                        <td style={{ color: customer.customer_balance > 0 ? "red": "inherit"}}>
                            <button className="btn btn-link p-0" onClick={() => handleShowEditModal(customer)}
                                    style={{ textDecoration: 'none'}}>
                            {customer.customer_name}
                            </button>
                        </td>
                        <td>{customer.location}</td>
                        <td>{customer.phone_number}</td>
                        {/*<td>{customer.customer_balance}</td>*/}
                        <td>{Number(customer.customer_balance ?? 0).toLocaleString()}</td>
                        <td>{customer.customer_balance_limit}</td>
                        <td>
                            {canViewCustomerReports(user) && <Button variant="outline-primary" onClick={() => navigate(`/CRM/customer-reports?customer_id=${customer.id}`)}>Insights</Button>}
                            {canEditCustomer() && (
                                <Button variant="success" onClick={() => openPayment(customer)} className="button-spacing">Receive Payment</Button>
                            )}
                            {canEditCustomer() && (
                                <Button variant="info" onClick={() => handleShowEditModal(customer)} className="button-spacing">Edit</Button>
                            )}
                            {canDeleteCustomer(customer) && (
                                <Button variant="danger" onClick={() => handleDeleteCustomer(customer.id)}>Delete</Button>
                            )}

                        </td>
                    </tr>
                ))}
                {!filteredCustomers.length && <tr><td colSpan={6} className="text-center text-muted py-4">No customers match these filters.</td></tr>}
                </tbody>
            </Table>}

            {canAddCustomer() && (
                <Modal show={showModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add Customer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row className="mb-3">
                                <Form.Group>
                                    <Form.Label column sm="4">Name</Form.Label>
                                    <Col sm="8">
                                        <Form.Control
                                            type="text"
                                            name="customer_name"
                                            value={newCustomer.customer_name}
                                            onChange={handleChange} required/>
                                    </Col>
                                </Form.Group>
                            </Row>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Location</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={newCustomer.location}
                                        onChange={handleChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Phone Number</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="phone_number"
                                        value={newCustomer.phone_number}
                                        onChange={handleChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Customer Limit</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance_limit"
                                        value={newCustomer.customer_balance_limit}
                                        onChange={handleChange}/>
                                    </Col>
                            </Form.Group>

                            <Button variant="success" type="submit">Add Customer</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}

            {/* Edit Customer Modal */}
            {canEditCustomer() && (
                <Modal show={showEditModal} onHide={handleCloseEditModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Customer</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form onSubmit={handleUpdateCustomer}>
                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Name</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="customer_name"
                                        value={selectedCustomer.customer_name}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Location</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={selectedCustomer.location}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Phone Number</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="phone_number"
                                        value={selectedCustomer.phone_number}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Balance</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance"
                                        value={selectedCustomer.customer_balance}
                                        // onChange={handleEditChange}/>
                                        readOnly
                                        plaintext
                                        />
                                </Col>
                            </Form.Group>

                            <Form.Group as="div" className="mb-3">
                                <Form.Label column sm="4">Customer Limit</Form.Label>
                                <Col sm="8">
                                    <Form.Control
                                        type="number"
                                        name="customer_balance_limit"
                                        value={selectedCustomer.customer_balance_limit}
                                        onChange={handleEditChange}/>
                                </Col>
                            </Form.Group>

                            <Button variant="success" type="submit">Update Customer</Button>
                        </Form>
                    </Modal.Body>
                </Modal>
            )}

            {/*modal for receiving payment*/}
            <Modal show={!!paymentCustomer} onHide={() => !paymentBusy && setPaymentCustomer(null)} centered>
                <Modal.Header closeButton={!paymentBusy}>
                    <Modal.Title>Receive Payment from {paymentCustomer?.customer_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {paymentError && <Alert variant="danger">{paymentError}</Alert>}
                    <p>Current balance: <strong>KES {formatAmount(paymentCustomer?.customer_balance)}</strong></p>
                    <Form>
                        <Form.Group as="div" className="mb-3">
                            <Form.Label>Payment Amount</Form.Label>
                            <Form.Control
                                type="number"
                                inputMode="decimal"
                                min="0.01"
                                step="0.01"
                                disabled={paymentBusy}
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                placeholder="Enter amount received"
                            />
                        </Form.Group>
                        <Form.Group as="div" className="mb-3">
                            <Form.Label>Payment Method</Form.Label>
                            <Form.Control
                                as="select"
                                disabled={paymentBusy}
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                            >
                                <option value="">Select Payment Method</option>
                                {paymentSettings.map((setting) => (
                                    <option key={setting.id} value={setting.account}>
                                        {setting.label_override || setting.account_name}
                                    </option>
                                ))}
                                {paymentSettings.length === 0 && (
                                    <>
                                        <option value="legacy:cash">Cash</option>
                                        <option value="legacy:mpesa">Mpesa Till</option>
                                        <option value="legacy:kcb">KCB</option>
                                        <option value="legacy:equity">Equity</option>
                                        <option value="legacy:george-mpesa">George Mpesa</option>
                                    </>
                                )}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="customer-payment-notes">
                            <Form.Label>Payment notes (optional)</Form.Label>
                            <Form.Control as="textarea" rows={3} maxLength={2000} value={paymentNotes}
                                disabled={paymentBusy} onChange={event => setPaymentNotes(event.target.value)}
                                placeholder="Reference, purpose or other payment details" />
                            <Form.Text>Included in the transaction and account ledger description.</Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" disabled={paymentBusy} onClick={() => setPaymentCustomer(null)}>Close</Button>
                    <Button variant="success" disabled={paymentBusy} onClick={() => handleReceivePayment(paymentCustomer)}>{paymentBusy ? 'Receiving…' : 'Receive Payment'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Customer;
